import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { replace } from 'ramda';
import { Button } from 'react-bootstrap';
import Alert from 'react-bootstrap/Alert';
import Form from 'react-bootstrap/Form';
import dropin from 'braintree-web-drop-in';
import braintree from 'braintree-web';
import { selectPay360 } from '../../features/pay360/pay360Slice';
import { selectPaypal } from '../../features/paypal/paypalSlice';
import { selectBasket } from '../../features/basket/basketSlice';
import { selectBraintree } from '../../features/braintree/braintreeSlice';
import { selectPendingRailcard } from '../../features/pendingRailcard/pendingRailcardSlice';
import { selectConfig } from '../../features/config/configSlice';
import { selectUser } from '../../features/user/userSlice';
import {
  APP_PAGES,
  CURRENCY_CODE,
  RAILCARD_TERMS_URLS,
  TRACKING_EVENTS,
  TRACKING_STEPS,
  Pay360SessionState,
} from '../../constants';
import type BasketState from '../../interfaces/BasketState';
import type BraintreeState from '../../interfaces/BraintreeState';
import type PendingRailcardState from '../../interfaces/PendingRailcardState';
import type Pay360State from '../../interfaces/Pay360State';
import type PaypalState from '../../interfaces/PaypalState';
import type UserState from '../../interfaces/UserState';
import type ConfigState from '../../interfaces/ConfigState';
import PaymentCardHosted from './components/PaymentCardHosted';
import Loader from '../../components/loader/Loader';
import BasketSummary from './components/BasketSummary';
import { trackEvent, getRailcardEventPayload } from '../../utils/googleTagManager';
import PaypalLogo from '../../images/paypal-logo.png';
import './Payment.scss';

const PayPalFlows = {
  CHECKOUT: 'checkout', // This is the standard one-time checkout flow, equivalent to what you'd expect with the default PayPal JavaScript SDK for immediate payment.
  VAULT: 'vault', // This is used when you want to vault (store) the PayPal account for future transactions. It requires billing agreements and setup for reference transactions.
};

const PayPalIntents = {
  AUTHORIZE: 'authorize', // Submits the transaction for authorization but not settlement.
  ORDER: 'order', // Validates the transaction without an authorization. Useful for capturing funds up to 90 days later. Only available for Checkout flow.
  SALE: 'sale', // Payment will be immediately submitted for settlement upon creating a transaction.
};

export default function Payment() {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { clientToken } = useSelector<PaypalState, PaypalState>(selectPaypal);

  const { loggedIn } = useSelector<UserState, UserState>(selectUser);

  const { brand, pay360Enabled, braintreeCardPaymentsEnabled, payPalEnabled } = useSelector<
    ConfigState,
    ConfigState
  >(selectConfig);

  const basket = useSelector<BasketState, BasketState>(selectBasket);

  const braintreeState = useSelector<BraintreeState, BraintreeState>(selectBraintree);

  const pendingRailcard = useSelector<PendingRailcardState, PendingRailcardState>(
    selectPendingRailcard,
  );

  const { error, paidFor: orderPaid, railcard } = pendingRailcard;

  const [termsAccepted] = useState(true); // implicit agreement when they hit 'pay'

  const railcardTermsUrl = RAILCARD_TERMS_URLS[brand];

  const {
    paymentProcessing,
    paymentProcessingFailed,
    paymentState,
    session,
    sessionState,
    transactionState,
  } = useSelector<Pay360State, Pay360State>(selectPay360);

  const redirectUrl = session?.redirectUrl;

  const [noRailcardFound, setNoRailcardFound] = useState(false);

  const { chosenPlan, selectedRailcard: basketRailcard } = basket;

  let basketAmount = 0;

  if (chosenPlan === 1) {
    basketAmount = basketRailcard?.oneYearPriceDiscounted || basketRailcard?.oneYearPrice;
  } else {
    basketAmount = basketRailcard?.threeYearPriceDiscounted || basketRailcard?.threeYearPrice;
  }

  const backToHome = () => {
    navigate(APP_PAGES.HOMEPAGE);
  };

  const goBack = () => {
    navigate(APP_PAGES.RAILCARD_PHOTO);
  };

  useEffect(() => {
    const paypalButton = document.querySelector('#paypal-button');

    let handlePaypalButtonClick;
    // Flag to track if the component has been unmounted.
    // This is needed to prevent errors when the component is unmounted in dev mode.
    let cancelled = false;

    if (railcard?.id) {
      if (clientToken) {
        // Braintree token
        braintree.client.create(
          {
            authorization: clientToken,
          },
          function (clientErr, clientInstance) {
            if (clientErr || cancelled) {
              if (clientErr) {
                console.error('Error creating client:', clientErr);
              }
              return;
            }

            braintree.paypal.create(
              {
                client: clientInstance,
              },
              function (paypalErr, paypalInstance) {
                if (paypalErr || cancelled) {
                  if (paypalErr) {
                    console.error('Error creating PayPal:', paypalErr);
                  }
                  return;
                }

                if (!paypalButton || cancelled) {
                  if (!paypalButton) {
                    console.warn('PayPal error: Paypal button not found');
                  }
                  return;
                }

                paypalButton.removeAttribute('disabled');

                // When the button is clicked, attempt to tokenize.
                handlePaypalButtonClick = function () {
                  // Because tokenization opens a popup, this has to be called as a result of
                  // customer action, like clicking a button. You cannot call this at any time.
                  paypalInstance.tokenize(
                    {
                      flow: PayPalFlows.CHECKOUT,
                      amount: (basketAmount / 100).toFixed(2),
                      currency: CURRENCY_CODE,
                      intent: PayPalIntents.SALE,
                    },
                    function (tokenizeErr, payload) {
                      if (cancelled) {
                        return;
                      }

                      if (tokenizeErr) {
                        if (tokenizeErr.type !== 'CUSTOMER') {
                          console.error('Error tokenizing:', tokenizeErr);
                        }
                        return;
                      }

                      // Tokenization succeeded
                      paypalButton.setAttribute('disabled', 'true');

                      dispatch({
                        type: 'paypal/checkout',
                        payload: {
                          amount: basketAmount,
                          railcardId: pendingRailcard.railcard.id,
                          deviceData: clientInstance._configuration.authorizationFingerprint,
                          nonce: payload.nonce,
                        },
                      });
                    },
                  );
                };

                if (cancelled) {
                  return;
                }
                paypalButton.addEventListener('click', handlePaypalButtonClick, false);
              },
            );
          },
        );
      }

      if (braintreeCardPaymentsEnabled) {
        dispatch({
          type: 'braintree/initiateCardPayments',
          payload: {
            amount: basketAmount,
            railcardId: pendingRailcard.railcard.id,
          },
        });
      }

      if (pay360Enabled) {
        dispatch({
          type: 'pay360/createSession',
          payload: {
            amount: basketAmount,
          },
        });
      }
    }

    return () => {
      cancelled = true;
      if (paypalButton && handlePaypalButtonClick) {
        paypalButton.removeEventListener('click', handlePaypalButtonClick, false);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps -- FEWTI-20: pre-existing. Adding the missing deps would re-fire this effect and change runtime behaviour; audit separately.
  }, [basketAmount, dispatch, railcard, clientToken]);

  useEffect(() => {
    if (!loggedIn) {
      navigate(APP_PAGES.HOMEPAGE);
    }

    // PayPal only, Pay360 handles this in the Sagas
    if (orderPaid) {
      const payload = getRailcardEventPayload({
        railcard: basketRailcard,
        chosenPlan: Number.parseInt(
          replace('Y', '', pendingRailcard.railcard.railcardExpirationLength),
        ),
      });

      trackEvent({
        brand,
        eventName: TRACKING_EVENTS.PAID,
        payload,
        railcardId: pendingRailcard.railcard.id,
        step: Object.assign(TRACKING_STEPS.PAYMENT_SCREEN, { stepName: pendingRailcard.paidWith }),
      });
      navigate(APP_PAGES.ORDER_SUMMARY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FEWTI-20: pre-existing. Adding the missing deps would re-fire this effect and change runtime behaviour; audit separately.
  }, [navigate, orderPaid, railcard, loggedIn]);

  useEffect(() => {
    setTimeout(function () {
      if (!railcard) {
        setNoRailcardFound(true);
      }
      // if no pending railcard after 5 seconds, assume none created and user needs to start again
    }, 5000);
  }, [railcard]);

  useEffect(() => {
    if (paymentState === Pay360SessionState.SUCCESS) {
      navigate(APP_PAGES.ORDER_SUMMARY);
    }
  }, [navigate, paymentState]);

  useEffect(() => {
    dispatch({ type: 'pay360/reset' });
    if (braintreeCardPaymentsEnabled) {
      dispatch({ type: 'braintree/reset' });
      dispatch({ type: 'braintree/getClientToken' });
    }
  }, [dispatch, braintreeCardPaymentsEnabled]);

  useEffect(() => {
    const braintreeSubmitButton = document.querySelector('#braintree-submit-button');

    let deviceData = null;

    if (!braintreeCardPaymentsEnabled || !braintreeState || !braintreeState.clientToken) {
      return;
    }

    braintree.client.create(
      {
        authorization: braintreeState.clientToken,
      },
      (err, clientInstance) => {
        if (err) {
          console.error('Error creating Braintree client:', err);
          return;
        }

        // Create Data Collector for deviceData
        braintree.dataCollector.create(
          { client: clientInstance, paypal: true }, // Enable PayPal fraud detection
          (err, dataCollectorInstance) => {
            if (!err) {
              deviceData = dataCollectorInstance.deviceData;
            }
          },
        );

        const threeDSecure = {
          amount: basketAmount / 100, // Convert to pounds for Braintree
        };

        dropin.create(
          {
            authorization: braintreeState.clientToken,
            selector: '#dropin-container',
            card: {
              cardholderName: { required: true },
              cvv: true,
            },
            threeDSecure,
          },
          function (err, dropinInstance) {
            if (err) {
              // Handle any errors that might've occurred when creating Drop-in
              console.error(err);
              return;
            }

            braintreeSubmitButton.addEventListener('click', function () {
              dropinInstance.requestPaymentMethod(function (err, payload) {
                const { nonce } = payload;

                dispatch({
                  type: 'braintree/checkout',
                  payload: {
                    amount: basketAmount,
                    railcardId: pendingRailcard.railcard.id,
                    deviceData,
                    nonce,
                  },
                });
              });
            });
          },
        );
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FEWTI-20: pre-existing. Adding the missing deps would re-fire this effect and change runtime behaviour; audit separately.
  }, [braintreeState]);

  const paymentEnabled = railcard && termsAccepted;

  const handleRenewalNotificationPreferenceChange = () => {
    dispatch({
      type: 'railcards/updateRailcardRenewalPreference',
      payload: {
        railcard,
        sendRenewalNotification: !railcard.sendRenewalNotification,
      },
    });
  };

  return (
    <div className="payment">
      <div className="mt-5">
        <h2 className="payment__subheading">Basket</h2>
        {railcard && <BasketSummary amount={basketAmount} railcard={railcard} />}

        <section className="payment__terms">
          <Form>
            <Form.Check
              type="switch"
              id="custom-switch"
              label="Remind me via email and app notification when my railcard is about to expire"
              checked={railcard?.sendRenewalNotification}
              onChange={handleRenewalNotificationPreferenceChange}
            />
          </Form>
          <p>
            By buying this Digital Railcard, I accept the{' '}
            <a
              rel="external noreferrer"
              className="external"
              target="_blank"
              href={railcardTermsUrl}
            >
              Railcard Terms and Conditions
            </a>
            {'. Please note that Digital Railcards are non-refundable.'}
          </p>
        </section>

        {termsAccepted && (
          <section>
            <h2 className="payment__subheading">Payments</h2>
            {paymentEnabled && (
              <ul className="payment-options" data-testid="payment-options">
                {payPalEnabled && (
                  <li className="payment-options__option">
                    <div className="paypal-cta-wrapper">
                      <img src={PaypalLogo} alt="" />
                      <div>
                        <Button id="paypal-button">Pay Now</Button>
                      </div>
                    </div>
                  </li>
                )}
                {pay360Enabled && (
                  <li className="payment-options__option">
                    <h3>Pay by card</h3>
                    {session ? (
                      <PaymentCardHosted
                        redirectUrl={redirectUrl}
                        basketAmount={basketAmount}
                        paymentProcessing={paymentProcessing}
                        paymentProcessingFailed={paymentProcessingFailed}
                        sessionState={sessionState}
                        transactionState={transactionState}
                      />
                    ) : (
                      <Loader message="" unwrapped={false} inline={false} />
                    )}
                  </li>
                )}

                {braintreeCardPaymentsEnabled && (
                  <li className="payment-options__option">
                    <h2>Pay with Card</h2>
                    <div id="dropin-container"></div>
                    <Button
                      disabled={braintreeState.paymentProcessing}
                      id="braintree-submit-button"
                    >
                      Pay Now
                    </Button>
                  </li>
                )}
              </ul>
            )}

            {!railcard && !error && !noRailcardFound && (
              <div>
                <Loader message="" unwrapped={false} inline={false} />
              </div>
            )}
            {!railcard && !error && noRailcardFound && (
              <div>
                <p>No railcard created for purchase. Please go back and select a railcard.</p>
                <Button className="back-to-home" onClick={backToHome} variant="primary">
                  Start again
                </Button>
              </div>
            )}
            {error && (
              <Alert className="mt-5" variant="danger">
                Sorry, an error has occurred while preparing your new railcard for payment. Please
                try again later.
              </Alert>
            )}
          </section>
        )}
      </div>

      <footer className="user-info__footer">
        <Button variant="outline-primary" onClick={goBack}>
          Back
        </Button>
      </footer>

      <div id="paypal-container"></div>
    </div>
  );
}
